/**
 * ColorPicker - Color palette picker with custom color input.
 *
 * Provides a dropdown palette of preset colors plus a custom color input.
 * Uses ColorSwatch atoms for the palette buttons.
 */

import { useState, useRef, useEffect } from 'react';
import { ColorSwatch } from '@/components/atoms';
import { TEAM_COLOR_PALETTE } from '@/lib/utils';
import { cn } from '@lean-agility/design-system/react';

export interface ColorPickerProps {
  /** Currently selected color */
  value: string;
  /** Callback when color changes */
  onChange: (color: string) => void;
  /** Color palette options (defaults to TEAM_COLOR_PALETTE) */
  palette?: string[];
  /** Whether to show custom color input */
  showCustomInput?: boolean;
  /** Trigger element (defaults to color indicator button) */
  trigger?: React.ReactNode;
  /** Additional CSS classes for the dropdown */
  className?: string;
}

/**
 * ColorPicker - Dropdown color palette with custom input.
 *
 * @example
 * ```tsx
 * <ColorPicker
 *   value={teamColor}
 *   onChange={setTeamColor}
 *   showCustomInput
 * />
 * ```
 */
export function ColorPicker({
  value,
  onChange,
  palette = TEAM_COLOR_PALETTE,
  showCustomInput = true,
  trigger,
  className,
}: ColorPickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const handleColorSelect = (color: string) => {
    onChange(color);
    setIsOpen(false);
  };

  const defaultTrigger = (
    <button
      type="button"
      onClick={() => setIsOpen(!isOpen)}
      className="
        flex size-10 shrink-0 items-center justify-center rounded-lg border border-border-default bg-bg-muted
        transition-transform
        hover:scale-105
        dark:border-border-default dark:bg-bg-subtle
      "
      title="Click to change color"
      aria-label="Open color picker"
      aria-expanded={isOpen}
    >
      <div className="size-5 rounded-full" style={{ backgroundColor: value }} />
    </button>
  );

  return (
    <div className="relative" ref={containerRef}>
      {trigger ? (
        <div
          onClick={() => setIsOpen(!isOpen)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && setIsOpen(!isOpen)}
        >
          {trigger}
        </div>
      ) : (
        defaultTrigger
      )}

      {/* Color Picker Dropdown */}
      {isOpen && (
        <div
          className={cn(
            `
              absolute top-12 left-0 z-50 rounded-lg border border-border-default bg-bg-elevated p-3 shadow-xl
              dark:border-border-default dark:bg-bg-elevated
            `,
            className,
          )}
          style={{ width: '220px' }}
        >
          <div className="grid grid-cols-6 gap-1.5">
            {palette.map((color) => (
              <ColorSwatch key={color} color={color} selected={value === color} onClick={handleColorSelect} />
            ))}
          </div>

          {/* Custom color input */}
          {showCustomInput && (
            <div
              className="
                mt-3 border-t border-border-default pt-3
                dark:border-border-default
              "
            >
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={value}
                  onChange={(e) => onChange(e.target.value)}
                  className="
                    h-7 flex-1 cursor-pointer rounded-sm border border-border-default
                    dark:border-border-default
                  "
                  title="Choose custom color"
                />
                <span
                  className="
                    font-mono text-xs text-text-muted
                    dark:text-text-muted
                  "
                >
                  {value}
                </span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
